<template>
  <div class="quantity-input" :class="`quantity-input--${size}`">
    <div class="quantity-input__controls">
      <button
        type="button"
        class="btn btn--icon-sm btn--gray quantity-input__button"
        :class="{ 'quantity-input__button--disabled': isDecreaseDisabled }"
        @click="handleButtonClick('decrease')"
        @mousedown="startLongPress('decrease')"
        @mouseup="stopLongPress"
        @mouseleave="stopLongPress"
        @touchstart="startLongPress('decrease')"
        @touchend="stopLongPress"
        :disabled="disabled || isDecreaseDisabled"
        :aria-label="`Verminder ${ariaLabel}`"
        :aria-controls="inputId"
      >
        <i class="icon icon-min"></i>
      </button>

      <div class="quantity-input__field-wrapper">
        <input
          :id="inputId"
          ref="inputField"
          type="number"
          :value="displayValue"
          :min="min"
          :max="max"
          :step="step"
          :disabled="disabled"
          :aria-label="ariaLabel"
          class="quantity-input__input"
          @input="handleInput"
          @focus="handleFocus"
          @blur="handleBlur"
          @keydown="handleKeydown"
          @paste="handlePaste"
        />
      </div>

      <button
        type="button"
        class="btn btn--icon-sm btn--gray quantity-input__button"
        @click="handleButtonClick('increase')"
        @mousedown="startLongPress('increase')"
        @mouseup="stopLongPress"
        @mouseleave="stopLongPress"
        @touchstart="startLongPress('increase')"
        @touchend="stopLongPress"
        :disabled="disabled"
        :aria-label="`Verhoog ${ariaLabel}`"
        :aria-controls="inputId"
      >
        <i class="icon icon-plus"></i>
      </button>
    </div>
  </div>
</template>

<script>
import { validateQuantity } from '../utils/useQuantityRules.js';

export default {
  name: 'QuantityInput',
  props: {
    value: {
      type: Number,
      required: true,
      validator: (value) => !isNaN(value) && isFinite(value),
    },
    min: {
      type: Number,
      default: 10,
      validator: (value) => !isNaN(value) && isFinite(value) && value >= 10,
    },
    max: {
      type: Number,
      default: 99999,
      validator: (value) => !isNaN(value) && isFinite(value) && value > 0,
    },
    step: {
      type: Number,
      default: 1,
      validator: (value) => !isNaN(value) && isFinite(value) && value > 0,
    },
    enforceIncrement: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: 'md',
      validator: (value) => ['sm', 'md', 'lg'].includes(value),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    ariaLabel: {
      type: String,
      default: 'Aantal pakketten',
    },
  },

  emits: ['update:value', 'quantity-changed', 'invalid', 'corrected'],

  data() {
    return {
      inputId: `quantity-input-${this._uid}`,
      displayValue: this.value.toString(),
      lastValidValue: this.value,

      longPressInterval: null,
      longPressStep: 1,
      longPressAcceleration: 0,
    };
  },

  computed: {
    isDecreaseDisabled() {
      return this.value <= this.min || this.disabled;
    },

    isIncreaseDisabled() {
      return this.value >= this.max || this.disabled;
    },

    normalizedStep() {
      return Math.max(1, Math.floor(this.step));
    },
  },

  watch: {
    value: {
      handler(newValue, oldValue) {
        if (newValue !== oldValue && !isNaN(newValue) && isFinite(newValue)) {
          this.lastValidValue = newValue;
          this.displayValue = newValue.toString();
        }
      },
      immediate: true,
    },

    min(newMin) {
      if (this.value < newMin) {
        this.$emit('update:value', newMin);
      }
    },

    max(newMax) {
      if (this.value > newMax) {
        this.$emit('update:value', newMax);
      }
    },
  },

  methods: {
    handleButtonClick(direction) {
      if (this.disabled) return;

      const delta =
        direction === 'increase' ? this.normalizedStep : -this.normalizedStep;
      this.changeQuantity(delta);
    },

    handleInput(event) {
      if (this.disabled) return;

      const input = event.target.value;
      this.displayValue = input;

      let trim = input.trim();
      let trimLength = trim.length <= 2 ? 3.5 : trim.length + 1.5;
      if (trim.length <= '99999'.length)
        event.target.style.minWidth = `${trimLength}ch`;

      // Don't validate during typing, only on blur
      // This allows typing "20" without jumping to "10" after typing "2"
    },

    handleFocus(event) {
      if (this.disabled) return;

      event.target.select();
    },

    handleBlur() {
      if (this.disabled) return;

      if (!this.displayValue.trim()) {
        this.restoreLastValidValue();
        return;
      }

      const validation = validateQuantity(
        this.displayValue,
        this.min,
        this.max,
        this.normalizedStep,
        this.enforceIncrement,
      );

      if (validation.isValid) {
        this.displayValue = validation.value.toString();
        if (validation.value !== this.value) {
          this.emitUpdate(validation.value);
        }
      } else {
        this.restoreLastValidValue();
      }
    },

    handleKeydown(event) {
      if (this.disabled) return;

      switch (event.key) {
        case 'Enter':
          event.preventDefault();
          this.$refs.inputField?.blur();
          break;
        case 'Escape':
          event.preventDefault();
          this.restoreLastValidValue();
          this.$refs.inputField?.blur();
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.handleButtonClick('increase');
          break;
        case 'ArrowDown':
          event.preventDefault();
          this.handleButtonClick('decrease');
          break;
        case '+':
          event.preventDefault();
          this.handleButtonClick('increase');
          break;
        case '-':
          event.preventDefault();
          this.handleButtonClick('decrease');
          break;
        case 'PageUp':
          event.preventDefault();
          this.changeQuantity(this.normalizedStep * 10);
          break;
        case 'PageDown':
          event.preventDefault();
          this.changeQuantity(-this.normalizedStep * 10);
          break;
      }
    },

    handlePaste(event) {
      if (this.disabled) return;

      event.preventDefault();
      const pastedText = (event.clipboardData || window.clipboardData).getData(
        'text',
      );

      if (!pastedText || typeof pastedText !== 'string') return;

      this.validateAndUpdate(pastedText);
    },

    changeQuantity(delta) {
      if (this.disabled) return;

      const newValue = Math.max(
        this.min,
        Math.min(this.max, this.value + delta),
      );

      if (newValue !== this.value && isFinite(newValue)) {
        this.emitUpdate(newValue);
      }
    },

    startLongPress(direction) {
      if (this.disabled) return;

      this.longPressStep = this.normalizedStep;
      this.longPressAcceleration = 0;

      const changeValue = () => {
        if (this.disabled) return;

        if (direction === 'increase') {
          this.changeQuantity(this.longPressStep);
        } else {
          this.changeQuantity(-this.longPressStep);
        }

        this.longPressAcceleration++;

        if (this.longPressAcceleration >= 2) {
          this.longPressStep = this.normalizedStep * 5;
        }
        if (this.longPressAcceleration >= 4) {
          this.longPressStep = this.normalizedStep * 10;
        }
      };

      this.longPressInterval = setInterval(changeValue, 400);
    },

    stopLongPress() {
      if (this.longPressInterval) {
        clearInterval(this.longPressInterval);
        this.longPressInterval = null;
      }
      this.longPressAcceleration = 0;
      this.longPressStep = this.normalizedStep;
    },

    emitUpdate(newValue) {
      if (!isFinite(newValue)) return;

      this.lastValidValue = newValue;
      this.displayValue = newValue.toString();

      this.$emit('update:value', newValue);
      this.$emit('quantity-changed', {
        from: this.value,
        to: newValue,
        step: this.normalizedStep,
        sourceComponent: 'QuantityInput',
        timestamp: Date.now(),
      });
    },

    restoreLastValidValue() {
      this.displayValue = this.lastValidValue.toString();
    },
  },

  beforeDestroy() {
    this.stopLongPress();
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  },
};
</script>
